import React, { useEffect, useMemo, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import { ReactComponent as LateFeeIcon } from 'assets/pricing/LateFee.svg'
import Divider from '@mui/material/Divider'
import { Box, CircularProgress, List, SvgIcon } from '@mui/material'
import Badge from '@mui/material/Badge'
import PropTypes from 'prop-types'
import { useLazyFetchAppliedLateFeesToInvoiceQuery } from 'api/pricing/lateFeesCrud'
import { get } from 'lodash'
import LateFeePopoverItem from 'components/pricing/LateFees/LateFeesListing/LateFeePopoverItem'

const MAX_LATE_FEES = 5

const LateFeesPopover = ({ invoiceId, lateFeeTargets = 0 }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [fetchAppliedLateFeesToInvoice, { data, isLoading, isFetching }] = useLazyFetchAppliedLateFeesToInvoiceQuery()
  const lateFees = useMemo(() => get(data, 'lateFees', []), [data])
  const loading = useMemo(() => isLoading || isFetching, [isLoading, isFetching])
  const handlePopoverOpen = event => {
    if (lateFeeTargets === 0) return
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = event => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const open = useMemo(() => Boolean(anchorEl), [anchorEl])
  const aria = open ? 'late-fees-popover' : undefined

  const limitedLateFees = lateFees.slice(0, MAX_LATE_FEES)
  const extraLateFees = lateFees.length - MAX_LATE_FEES

  useEffect(() => {
    if (open && invoiceId) {
      fetchAppliedLateFeesToInvoice({ id: invoiceId })
    }
  }, [open, invoiceId])

  return (
    <>
      <IconButton aria-describedby={aria} disabled={!lateFeeTargets} onMouseEnter={handlePopoverOpen} onMouseLeave={handlePopoverClose}>
        <Badge badgeContent={lateFeeTargets} color="error">
          <SvgIcon
            sx={{
              fontSize: 26,
            }}
          >
            <LateFeeIcon />
          </SvgIcon>
        </Badge>
      </IconButton>
      <Popover
        sx={{ pointerEvents: 'none' }}
        slotProps={{ paper: { sx: { minWidth: 500 } } }}
        id={aria}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableRestoreFocus
      >
        {loading && (
          <Box p={2} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <List
            sx={{
              py: 0,
              minWidth: 360,
            }}
          >
            {limitedLateFees.map((lateFee, index) => (
              <>
                <LateFeePopoverItem lateFee={lateFee} />
                {index < limitedLateFees.length - 1 && <Divider />}
              </>
            ))}
            {extraLateFees > 0 && (
              <>
                <Divider />
                <Typography m={2} variant="caption" display="flex" justifyContent="end">
                  {`+${extraLateFees} more`}
                </Typography>
              </>
            )}
          </List>
        )}
      </Popover>
    </>
  )
}

LateFeesPopover.propTypes = {
  invoiceId: PropTypes.string,
  lateFeeTargets: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default LateFeesPopover
